/**
 * Slide viewer.
 *
 * One slide at a time, the way the deck is meant to be read, with a grid
 * overview for jumping around. Videos are not embedded in the page — a hotspot
 * over the slide opens the player, which matches how the deck behaves in
 * PowerPoint and keeps 43 slides from holding seven video elements open.
 */
import { api, deckSlug } from './api.ts';
import { openPlayer, type VideoAsset } from './player.ts';

export interface Hotspot {
  x: number;
  y: number;
  w: number;
  h: number;
  videoId: string;
  marker: { x: number; y: number; w: number; h: number };
}

export interface ManifestSlide {
  n: number;
  image: string;
  image1x: string;
  width: number;
  height: number;
  lqip: string;
  text: string[];
  hotspots: Hotspot[];
}

export interface Manifest {
  id: string;
  /** Absent on a bundle built before versioning. */
  version?: number;
  /**
   * Sent ONLY when the server granted an admin preview — its presence is what
   * makes this a preview, never a guess made from the version number.
   * `publishedVersion` is null when nothing is published yet.
   */
  preview?: { version: number; publishedVersion: number | null };
  title: string;
  subtitle?: string;
  confidentialNotice?: string;
  aspect: number;
  slideCount: number;
  slides: ManifestSlide[];
  videos: VideoAsset[];
}

const ICONS = {
  prev: '<svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7"/></svg>',
  next: '<svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>',
  grid: '<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>',
  expand: '<svg viewBox="0 0 24 24"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>',
  close: '<svg viewBox="0 0 24 24"><path d="M5 5l14 14M19 5L5 19"/></svg>',
  exit: '<svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2v-4M15 3h4a2 2 0 0 1 2 2v4M3 9V5a2 2 0 0 1 2-2h4M21 15v4a2 2 0 0 1-2 2h-4"/></svg>',
  playSmall: '<svg viewBox="0 0 24 24"><path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z"/></svg>',
  rotate:
    '<svg viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="10" rx="2"/><path d="M8 3.5A6 6 0 0 1 14 2M8 3.5l2 1M8 3.5l-.6 2"/></svg>',
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, html?: string) {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (html !== undefined) n.innerHTML = html;
  return n;
}

export function mediaUrl(rel: string): string {
  // Scoped to this deck. Manifest paths are relative to the deck's own prefix,
  // so an unscoped /media/ URL resolves against whichever deck the server
  // guesses — which is to say, none.
  return `/media/${encodeURIComponent(deckSlug)}/${rel.split('/').map(encodeURIComponent).join('/')}`;
}

export class DeckViewer {
  private index = 0;
  private root: HTMLElement;
  private frame!: HTMLElement;
  private img!: HTMLImageElement;
  private lqip!: HTMLImageElement;
  private hotspotLayer!: HTMLElement;
  private counter!: HTMLButtonElement;
  private progressFill!: HTMLElement;
  private prevBtn!: HTMLButtonElement;
  private nextBtn!: HTMLButtonElement;
  private deckEl!: HTMLElement;
  private gridEl: HTMLElement | null = null;
  private playerOpen = false;

  private slideEnteredAt = 0;
  private pending = {
    slideSeconds: {} as Record<string, number>,
    videoSeconds: {} as Record<string, number>,
    videoPlays: [] as string[],
    maxSlide: 1,
    /**
     * Every slide shown since the last flush. Reported alongside `maxSlide`
     * because the two answer different questions: the server used to derive
     * "slides seen" from the high-water mark alone, which recorded only the
     * furthest slide at each flush and so undercounted everyone who read at a
     * steady pace or went back.
     */
    slides: new Set<number>(),
  };
  private flushTimer = 0;

  constructor(
    private readonly manifest: Manifest,
    root: HTMLElement,
    private readonly onLogout: () => void,
  ) {
    this.root = root;
    this.build();
    this.index = this.slideFromHash();
    this.show(this.index, true);
    this.bind();
    this.slideEnteredAt = performance.now();
    this.flushTimer = window.setInterval(() => this.flush(), 15_000);
  }

  // -- build ---------------------------------------------------------------

  private build(): void {
    const deck = el('div', 'deck');
    this.deckEl = deck;
    deck.style.setProperty('--deck-aspect', String(this.manifest.aspect));

    // --- top bar ---
    const bar = el('div', 'deck-bar');
    const title = el('div', 'deck-title');
    title.append(document.createTextNode('Purpose'), Object.assign(el('span'), { textContent: 'Book' }));
    bar.appendChild(title);
    if (this.manifest.subtitle) {
      const sub = el('div', 'deck-sub');
      sub.textContent = `· ${this.manifest.subtitle}`;
      bar.appendChild(sub);
    }
    bar.appendChild(el('div', 'deck-bar-spacer'));

    if (this.manifest.confidentialNotice) {
      const conf = el('div', 'confidential');
      conf.textContent = this.manifest.confidentialNotice;
      bar.appendChild(conf);
    }

    this.counter = el('button', 'counter') as HTMLButtonElement;
    this.counter.setAttribute('aria-label', 'All slides');
    this.counter.addEventListener('click', () => this.toggleGrid());
    bar.appendChild(this.counter);

    const gridBtn = el('button', 'icon-btn', ICONS.grid) as HTMLButtonElement;
    gridBtn.setAttribute('aria-label', 'All slides (G)');
    gridBtn.addEventListener('click', () => this.toggleGrid());
    bar.appendChild(gridBtn);

    const fsBtn = el('button', 'icon-btn', ICONS.expand) as HTMLButtonElement;
    fsBtn.setAttribute('aria-label', 'Present fullscreen (F)');
    fsBtn.addEventListener('click', () => void this.toggleFullscreen());
    bar.appendChild(fsBtn);

    const out = el('button', 'icon-btn', ICONS.close) as HTMLButtonElement;
    out.setAttribute('aria-label', 'Sign out');
    out.addEventListener('click', () => {
      this.flush();
      this.onLogout();
    });
    bar.appendChild(out);
    deck.appendChild(bar);

    // --- stage ---
    const stage = el('div', 'stage');
    this.frame = el('div', 'slide-frame');

    this.lqip = document.createElement('img');
    this.lqip.className = 'slide-lqip';
    this.lqip.alt = '';
    this.lqip.setAttribute('aria-hidden', 'true');

    this.img = document.createElement('img');
    this.img.className = 'slide-img';
    this.img.decoding = 'async';

    this.hotspotLayer = el('div');
    this.hotspotLayer.style.position = 'absolute';
    this.hotspotLayer.style.inset = '0';

    this.frame.append(this.lqip, this.img, this.hotspotLayer);
    stage.appendChild(this.frame);

    this.prevBtn = el('button', 'nav-btn nav-prev', ICONS.prev) as HTMLButtonElement;
    this.prevBtn.setAttribute('aria-label', 'Previous slide');
    this.prevBtn.addEventListener('click', () => this.go(-1));
    this.nextBtn = el('button', 'nav-btn nav-next', ICONS.next) as HTMLButtonElement;
    this.nextBtn.setAttribute('aria-label', 'Next slide');
    this.nextBtn.addEventListener('click', () => this.go(1));
    stage.append(this.prevBtn, this.nextBtn);

    deck.appendChild(stage);

    // --- progress ---
    const progress = el('div', 'progress');
    progress.setAttribute('role', 'progressbar');
    this.progressFill = el('div', 'progress-fill');
    progress.appendChild(this.progressFill);
    progress.addEventListener('click', (e) => {
      const r = progress.getBoundingClientRect();
      const p = (e.clientX - r.left) / r.width;
      this.show(Math.min(this.manifest.slides.length - 1, Math.max(0, Math.round(p * (this.manifest.slides.length - 1)))));
    });
    deck.appendChild(progress);

    // A 16:9 slide in a portrait phone viewport is only a couple of hundred
    // pixels tall, which is too small to read. Turning the device roughly
    // doubles it, so say so once — and let it be dismissed.
    const hint = el('div', 'rotate-hint', ICONS.rotate);
    const hintText = el('span');
    hintText.textContent = 'Turn your phone sideways for a larger view';
    const dismiss = el('button') as HTMLButtonElement;
    dismiss.textContent = 'Dismiss';
    dismiss.addEventListener('click', () => deck.classList.add('hint-dismissed'));
    hint.append(hintText, dismiss);
    deck.appendChild(hint);

    this.root.replaceChildren(deck);
  }

  // -- navigation ----------------------------------------------------------

  private slideFromHash(): number {
    const m = /^#(\d+)$/.exec(location.hash);
    if (!m) return 0;
    const n = Number(m[1]);
    return Number.isFinite(n) ? Math.min(this.manifest.slides.length - 1, Math.max(0, n - 1)) : 0;
  }

  private go(delta: number): void {
    this.show(this.index + delta);
  }

  show(i: number, initial = false): void {
    const clamped = Math.min(this.manifest.slides.length - 1, Math.max(0, i));
    if (!initial && clamped === this.index) return;

    if (!initial) this.recordDwell();
    this.index = clamped;
    const slide = this.manifest.slides[clamped]!;

    this.lqip.src = slide.lqip;
    this.lqip.classList.remove('hide');
    this.img.classList.remove('ready');

    const src2x = mediaUrl(slide.image);
    const src1x = mediaUrl(slide.image1x);
    this.img.srcset = `${src1x} ${Math.round(slide.width / 2)}w, ${src2x} ${slide.width}w`;
    this.img.sizes = '100vw';
    this.img.src = src2x;
    this.img.alt = slide.text.length ? `Slide ${slide.n}: ${slide.text.slice(0, 3).join(' — ')}` : `Slide ${slide.n}`;

    const reveal = () => {
      this.img.classList.add('ready');
      this.lqip.classList.add('hide');
    };
    if (this.img.complete) reveal();
    else this.img.onload = reveal;

    this.renderHotspots(slide);
    this.counter.textContent = `${slide.n} / ${this.manifest.slideCount}`;
    this.progressFill.style.width = `${((clamped + 1) / this.manifest.slides.length) * 100}%`;
    this.prevBtn.disabled = clamped === 0;
    this.nextBtn.disabled = clamped === this.manifest.slides.length - 1;

    history.replaceState(null, '', `#${slide.n}`);
    this.pending.maxSlide = Math.max(this.pending.maxSlide, slide.n);
    this.pending.slides.add(slide.n);
    this.slideEnteredAt = performance.now();
    this.preload(clamped);
    if (this.gridEl) this.syncGridSelection();
  }

  /** Warm the neighbours so paging feels instant. */
  private preload(i: number): void {
    for (const j of [i + 1, i + 2, i - 1]) {
      const s = this.manifest.slides[j];
      if (!s) continue;
      const im = new Image();
      im.src = mediaUrl(s.image);
    }
  }

  private renderHotspots(slide: ManifestSlide): void {
    const nodes = slide.hotspots.map((h) => {
      const video = this.manifest.videos.find((v) => v.id === h.videoId);
      const b = el('button', 'hotspot') as HTMLButtonElement;
      b.style.left = `${h.x * 100}%`;
      b.style.top = `${h.y * 100}%`;
      b.style.width = `${h.w * 100}%`;
      b.style.height = `${h.h * 100}%`;
      b.setAttribute('aria-label', video ? `Play video: ${video.title}` : 'Play video');
      b.title = video ? video.title : 'Play video';

      // The badge sits on the original play-button position, not the centre of
      // the enlarged hit area, so it stays where the deck put it.
      const badge = el('div', 'hotspot-badge', ICONS.playSmall);
      const relX = h.w > 0 ? (h.marker.x + h.marker.w / 2 - h.x) / h.w : 0.5;
      const relY = h.h > 0 ? (h.marker.y + h.marker.h / 2 - h.y) / h.h : 0.5;
      badge.style.left = `${Math.min(0.92, Math.max(0.08, relX)) * 100}%`;
      badge.style.top = `${Math.min(0.92, Math.max(0.08, relY)) * 100}%`;
      b.appendChild(badge);

      b.addEventListener('click', (e) => {
        e.stopPropagation();
        if (video) this.play(video);
      });
      return b;
    });
    this.hotspotLayer.replaceChildren(...nodes);
  }

  private play(video: VideoAsset): void {
    if (this.playerOpen) return;
    this.playerOpen = true;
    this.pending.videoPlays.push(video.id);
    openPlayer(
      video,
      {
        mediaUrl,
        onWatched: (id, seconds) => {
          this.pending.videoSeconds[id] = (this.pending.videoSeconds[id] ?? 0) + seconds;
        },
      },
      () => {
        this.playerOpen = false;
        this.slideEnteredAt = performance.now();
      },
    );
  }

  // -- grid ----------------------------------------------------------------

  private toggleGrid(): void {
    if (this.gridEl) {
      this.gridEl.remove();
      this.gridEl = null;
      return;
    }
    const overlay = el('div', 'grid-overlay');
    const head = el('div', 'grid-head');
    const h = el('h2');
    h.textContent = `All slides — ${this.manifest.slideCount}`;
    const close = el('button', 'icon-btn', ICONS.close) as HTMLButtonElement;
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', () => this.toggleGrid());
    head.append(h, close);

    const grid = el('div', 'grid');
    this.manifest.slides.forEach((s, i) => {
      const t = el('button', 'thumb') as HTMLButtonElement;
      t.dataset.i = String(i);
      const im = document.createElement('img');
      im.loading = 'lazy';
      im.decoding = 'async';
      im.src = mediaUrl(s.image1x);
      im.alt = `Slide ${s.n}`;
      t.appendChild(im);
      if (s.hotspots.length) {
        t.appendChild(el('div', 'thumb-play', ICONS.playSmall));
      }
      const n = el('div', 'thumb-n');
      n.textContent = String(s.n);
      t.appendChild(n);
      t.addEventListener('click', () => {
        this.show(i);
        this.toggleGrid();
      });
      grid.appendChild(t);
    });

    overlay.append(head, grid);
    document.body.appendChild(overlay);
    this.gridEl = overlay;
    this.syncGridSelection();
    close.focus();
  }

  private syncGridSelection(): void {
    if (!this.gridEl) return;
    for (const t of this.gridEl.querySelectorAll<HTMLElement>('.thumb')) {
      t.classList.toggle('current', Number(t.dataset.i) === this.index);
    }
    this.gridEl.querySelector('.thumb.current')?.scrollIntoView({ block: 'nearest' });
  }

  // -- input ---------------------------------------------------------------

  private bind(): void {
    document.addEventListener('keydown', (e) => {
      if (this.playerOpen) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
          this.go(1);
          break;
        case 'ArrowLeft':
        case 'PageUp':
          this.go(-1);
          break;
        case 'Home':
          this.show(0);
          break;
        case 'End':
          this.show(this.manifest.slides.length - 1);
          break;
        case 'g':
        case 'G':
          this.toggleGrid();
          break;
        case 'f':
        case 'F':
          void this.toggleFullscreen();
          break;
        case 'Escape':
          if (this.gridEl) this.toggleGrid();
          else if (document.fullscreenElement) void document.exitFullscreen();
          break;
        default:
          return;
      }
      e.preventDefault();
    });

    // Swipe.
    let sx = 0;
    let sy = 0;
    let tracking = false;
    this.frame.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        sx = e.touches[0]!.clientX;
        sy = e.touches[0]!.clientY;
        tracking = true;
      },
      { passive: true },
    );
    this.frame.addEventListener(
      'touchend',
      (e) => {
        if (!tracking) return;
        tracking = false;
        const t = e.changedTouches[0]!;
        const dx = t.clientX - sx;
        const dy = t.clientY - sy;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.6) this.go(dx < 0 ? 1 : -1);
      },
      { passive: true },
    );

    // A pasted or edited "#12" should move the deck. `show()` uses
    // replaceState, which does not fire hashchange, so there is no loop — and
    // Back still leaves the deck rather than walking 43 slides backwards.
    window.addEventListener('hashchange', () => {
      const n = this.slideFromHash();
      if (n !== this.index) this.show(n);
    });

    document.addEventListener('fullscreenchange', () => {
      this.deckEl.classList.toggle('immersive', !!document.fullscreenElement);
    });

    window.addEventListener('pagehide', () => this.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
      else this.slideEnteredAt = performance.now();
    });
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      /* nothing useful to do */
    }
  }

  // -- engagement ----------------------------------------------------------

  private recordDwell(): void {
    if (!this.slideEnteredAt) return;
    const secs = (performance.now() - this.slideEnteredAt) / 1000;
    // Ignore flicks through the deck; they say nothing about attention.
    if (secs >= 1) {
      const key = String(this.manifest.slides[this.index]!.n);
      this.pending.slideSeconds[key] = (this.pending.slideSeconds[key] ?? 0) + Math.round(secs * 10) / 10;
    }
    this.slideEnteredAt = performance.now();
  }

  private flush(): void {
    this.recordDwell();
    const p = this.pending;
    const hasData =
      Object.keys(p.slideSeconds).length || Object.keys(p.videoSeconds).length || p.videoPlays.length || p.slides.size;
    if (!hasData) return;

    api.events({
      slide: p.maxSlide,
      slides: [...p.slides].sort((a, b) => a - b),
      slideSeconds: p.slideSeconds,
      videoSeconds: p.videoSeconds,
      videoPlays: p.videoPlays,
      totalSeconds: Object.values(p.slideSeconds).reduce((a, b) => a + b, 0),
    });

    this.pending = { slideSeconds: {}, videoSeconds: {}, videoPlays: [], maxSlide: p.maxSlide, slides: new Set() };
  }

  destroy(): void {
    this.flush();
    window.clearInterval(this.flushTimer);
  }
}
